import React, { useEffect, useState, useRef } from 'react';
import Dashboard from './pages/Dashboard';
import EmployeeDashboard from './pages/EmployeeDashboard';
import Events from './pages/Events';
import Venues from './pages/Venues';
import Attendees from './pages/Attendees';
import Schedule from './pages/Schedule';
import Inventory from './pages/Inventory';
import Meals from './pages/Meals';
import Budget from './pages/Budget';
import Tasks from './pages/Tasks';
import EmployeeInvoices from './pages/EmployeeInvoices';
import AdminInvoices from './pages/AdminInvoices';
import EmergencyContacts from './pages/EmergencyContacts';
import Profile from './pages/Profile';
import Gallery from './pages/Gallery';
import Ethos from './pages/Ethos';
import Feedback from './pages/Feedback';
import TeamGames from './pages/TeamGames';
import OnboardingApp from './pages/OnboardingApp';
import Landing from './pages/Landing';
import LinksViewer from './pages/LinksViewer';
import { getSupabaseClient, signInWithGoogle, signOutAuth, usingSupabase, getAll, put, generateId } from './hooks/useDB';
import { setTokenProvider } from './authToken';
import { canAccessApp, canAccessLinksViewer, canManageLinks, canDeleteLinks, canAccessTeamGames, getUserRole } from './auth';
import { resetTeamGamesState } from './data/teams';
import AskPlanner from './components/AskPlanner';
import EthosWelcome from './components/EthosWelcome';
import { LogoFull, LogoMark } from './components/Logo';
import TopbarCountdown from './components/TopbarCountdown';
import Toaster from './components/Toaster';
import ConfirmDialog from './components/ConfirmDialog';
import {
  LayoutDashboard, Calendar, MapPin, Users,
  Clock, Package, UtensilsCrossed, DollarSign, CheckSquare,
  Menu, X, Receipt, Banknote, PhoneCall, Bot, UserCircle, Images,
  ChevronLeft, ChevronRight, LogOut, Sparkles, MessageSquare, Home, Gamepad2,
} from 'lucide-react';
import './App.css';

// ── Firebase Identity Platform (Google SSO) ───────────────────────────────────
// Used for sign-in only. All data lives in Supabase regardless of auth provider.
const usingFirebaseAuth = Boolean(process.env.REACT_APP_FIREBASE_API_KEY);

let firebaseAuth = null;
let firebaseSignInWithPopup = null;
let firebaseGoogleProvider = null;
let firebaseOnAuthStateChanged = null;
let firebaseSignOut = null;

if (usingFirebaseAuth) {
  (async () => {
    const { initializeApp } = await import('firebase/app');
    const { getAuth, signInWithPopup, GoogleAuthProvider, onAuthStateChanged, signOut } = await import('firebase/auth');

    const app = initializeApp({
      apiKey:    process.env.REACT_APP_FIREBASE_API_KEY,
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
      projectId:  process.env.REACT_APP_FIREBASE_PROJECT_ID,
    });

    firebaseAuth = getAuth(app);
    firebaseSignInWithPopup = signInWithPopup;
    firebaseGoogleProvider = new GoogleAuthProvider();
    firebaseGoogleProvider.setCustomParameters({ hd: 'nativeorange.ai' });
    firebaseOnAuthStateChanged = onAuthStateChanged;
    firebaseSignOut = signOut;

    // Let the data layer authenticate backend API calls with the Firebase token.
    setTokenProvider(async () => {
      const u = firebaseAuth?.currentUser;
      return u ? u.getIdToken() : null;
    });
  })();
}

const NAV = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'employee'] },
  { id: 'events',    label: 'Events',    icon: Calendar,         roles: ['admin', 'employee'] },
  { id: 'venues',    label: 'Venues',    icon: MapPin,           roles: ['admin'] },
  { id: 'attendees', label: 'Attendees', icon: Users,            roles: ['admin'] },
  { id: 'schedule',  label: 'Itinerary', icon: Clock,            roles: ['admin', 'employee'] },
  { id: 'contacts',  label: 'Call POCs', icon: PhoneCall,        roles: ['admin', 'employee'] },
  { id: 'inventory', label: 'Inventory', icon: Package,          roles: ['admin'] },
  { id: 'meals',     label: 'Meals',     icon: UtensilsCrossed,  roles: ['admin'] },
  { id: 'budget',    label: 'Budget',    icon: DollarSign,       roles: ['admin'] },
  { id: 'invoices',  label: 'My Invoices',icon: Receipt,         roles: ['admin', 'employee'] },
  { id: 'reimbursements', label: 'Reimbursements', icon: Banknote, roles: ['admin'] },
  { id: 'tasks',     label: 'Tasks',     icon: CheckSquare,      roles: ['admin'] },
  { id: 'gallery',   label: 'Gallery',   icon: Images,           roles: ['admin', 'employee'] },
  { id: 'games',     label: 'Team Games',icon: Gamepad2,         roles: ['admin'], gate: 'teamGames' },
  { id: 'feedback',  label: 'Feedback',  icon: MessageSquare,    roles: ['admin', 'employee'] },
  { id: 'ethos',     label: 'Our Ethos', icon: Sparkles,         roles: ['admin', 'employee'] },
];

const PAGES = {
  dashboard: Dashboard,
  events: Events,
  venues: Venues,
  attendees: Attendees,
  schedule: Schedule,
  inventory: Inventory,
  meals: Meals,
  budget: Budget,
  invoices: EmployeeInvoices,
  reimbursements: AdminInvoices,
  contacts: EmergencyContacts,
  tasks: Tasks,
  gallery: Gallery,
  games: TeamGames,
  feedback: Feedback,
  ethos: Ethos,
  profile: Profile,
};

export default function App() {
  const [appMode, setAppMode] = useState(null); // null = landing, 'event-planner', 'links-viewer'
  const [active, setActive] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [plannerOpen, setPlannerOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(usingSupabase() || usingFirebaseAuth);
  const [authError, setAuthError] = useState('');
  const [showProfileDrawer, setShowProfileDrawer] = useState(false);
  const [showEthosWelcome, setShowEthosWelcome] = useState(false);
  const [profileForm, setProfileForm] = useState({
    dietary: 'Not specified',
    bloodGroup: 'Unknown',
    originCity: '',
    phone: '',
    address: '',
    birthday: '',
    tshirtSize: 'M',
    roomNeeded: false,
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelation: 'Parent',
  });

  const userMenuRef = useRef(null);

  const supabase = getSupabaseClient();

  const user = session?.user || null;
  const userName = user?.displayName || user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email?.split('@')?.[0] || 'Local User';
  const userEmail = user?.email || '';
  // When no auth provider is configured everyone is treated as admin so all nav items are visible
  const isAuthMode = usingSupabase() || usingFirebaseAuth;
  const userRole = isAuthMode ? getUserRole(userEmail) : 'admin';
  // Links Viewer is granted per-email, so some employees have more than one app
  const linksViewerAllowed = isAuthMode ? canAccessLinksViewer(userEmail) : true;
  const teamGamesAllowed = isAuthMode ? canAccessTeamGames(userEmail) : true;
  const appAccess = { 'links-viewer': linksViewerAllowed };
  const hasAppHub = userRole === 'admin' || linksViewerAllowed;
  const canOpenPage = (pageId) => {
    const item = NAV.find(nav => nav.id === pageId);
    if (!item) return true;   // not a nav-gated page (e.g. profile)
    return item.roles.includes(userRole) && (item.gate !== 'teamGames' || teamGamesAllowed);
  };
  const visibleNav = NAV.filter(item => canOpenPage(item.id));
  // `active` is not only set by clicking the sidebar — a planner:navigate event
  // can set it to any id in PAGES. Resolving straight from PAGES[active] would
  // therefore render a page the user is not allowed to see and never had a link
  // to, so the same rule that filters the nav has to gate the render as well.
  let Page = (canOpenPage(active) ? PAGES[active] : null) || PAGES[visibleNav[0]?.id] || Events;
  // Employees get a personalized dashboard; admins keep the command center
  if (active === 'dashboard' && userRole !== 'admin') Page = EmployeeDashboard;

  const userInitials = userName.split(' ').filter(Boolean).slice(0, 2).map(n => n.charAt(0).toUpperCase()).join('') || 'U';

  // ── Close user menu on outside click ─────────────────────────────────────
  useEffect(() => {
    if (!userMenuOpen) return;
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [userMenuOpen]);

  // ── Firebase (Google SSO) auth listener ──────────────────────────────────
  useEffect(() => {
    if (!usingFirebaseAuth) return;
    let unsubscribe = null;
    let attempts = 0;
    const MAX_ATTEMPTS = 20;
    const trySubscribe = () => {
      if (firebaseAuth && firebaseOnAuthStateChanged) {
        unsubscribe = firebaseOnAuthStateChanged(firebaseAuth, (fbUser) => {
          setSession(fbUser ? { user: fbUser } : null);
          setAuthLoading(false);
        });
      } else if (attempts < MAX_ATTEMPTS) {
        attempts++;
        setTimeout(trySubscribe, 100);
      } else {
        setAuthLoading(false);
      }
    };
    trySubscribe();
    return () => { if (unsubscribe) unsubscribe(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Supabase auth listener (used when Firebase SSO isn't configured) ──────
  useEffect(() => {
    if (usingFirebaseAuth || !supabase) return;
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session || null);
      setAuthLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, currentSession) => {
      setSession(currentSession || null);
      setAuthLoading(false);
    });
    return () => { mounted = false; subscription.unsubscribe(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supabase]);

  useEffect(() => {
    if (authLoading) return;
    // 'profile' is not in NAV (accessed via user menu) — don't redirect away from it
    if (active === 'profile') return;
    if (visibleNav.length && !visibleNav.some(item => item.id === active)) {
      setActive(visibleNav[0].id);
    }
  }, [active, visibleNav, authLoading]);

  // Employees with only one app skip the landing page and go straight to the event planner
  useEffect(() => {
    if (authLoading || !userEmail) return;
    if (!hasAppHub && appMode === null) {
      setAppMode('event-planner');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, userEmail, hasAppHub]);

  useEffect(() => {
    const handleNavigation = (e) => {
      const pageId = e.detail;
      if (PAGES[pageId]) setActive(pageId);
    };
    window.addEventListener('planner:navigate', handleNavigation);
    return () => window.removeEventListener('planner:navigate', handleNavigation);
  }, []);

  useEffect(() => {
    const openProfile = () => {
      getAll('attendees').then(list => {
        const existing = list.find(a => (a.email || '').toLowerCase() === userEmail.toLowerCase());
        setProfileForm({
          dietary: existing?.dietary || 'Not specified',
          bloodGroup: existing?.bloodGroup || 'Unknown',
          originCity: existing?.originCity || '',
          phone: existing?.phone || '',
          address: existing?.address || '',
          birthday: existing?.birthday || '',
          tshirtSize: existing?.tshirtSize || 'M',
          roomNeeded: existing?.roomNeeded || false,
          emergencyContactName: existing?.emergencyContactName || '',
          emergencyContactPhone: existing?.emergencyContactPhone || '',
          emergencyContactRelation: existing?.emergencyContactRelation || 'Parent',
        });
        setShowProfileDrawer(true);
      }).catch(() => setShowProfileDrawer(true));
    };
    window.addEventListener('planner:open-profile', openProfile);
    return () => window.removeEventListener('planner:open-profile', openProfile);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail]);

  // ── Profile completion prompt ─────────────────────────────────────────────
  useEffect(() => {
    if (authLoading || !userEmail) return;
    // Skip if the user has already explicitly saved their profile
    try { if (localStorage.getItem(`no-profile-done:${userEmail}`)) return; } catch { /* ignore */ }
    getAll('attendees').then(list => {
      const existing = list.find(a => (a.email || '').toLowerCase() === userEmail.toLowerCase());
      // If they already have a phone number they've updated their profile before — suppress and remember
      if (existing?.phone) {
        try { localStorage.setItem(`no-profile-done:${userEmail}`, '1'); } catch { /* ignore */ }
        return;
      }
      const incomplete =
        !existing ||
        !existing.bloodGroup || existing.bloodGroup === 'Unknown' ||
        !existing.originCity ||
        !existing.dietary || existing.dietary === 'Not specified' ||
        !existing.emergencyContactName;
      if (incomplete) {
        setProfileForm({
          dietary: existing?.dietary && existing.dietary !== 'Not specified' ? existing.dietary : 'Not specified',
          bloodGroup: existing?.bloodGroup || 'Unknown',
          originCity: existing?.originCity || '',
          phone: existing?.phone || '',
          address: existing?.address || '',
          birthday: existing?.birthday || '',
          tshirtSize: existing?.tshirtSize || 'M',
          roomNeeded: existing?.roomNeeded || false,
          emergencyContactName: existing?.emergencyContactName || '',
          emergencyContactPhone: existing?.emergencyContactPhone || '',
          emergencyContactRelation: existing?.emergencyContactRelation || 'Parent',
        });
        setShowProfileDrawer(true);
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, userEmail]);

  // ── Ethos welcome — show once per user on first sign-in ───────────────────
  useEffect(() => {
    if (authLoading) return;
    const key = `no-ethos-seen:${userEmail || 'local'}`;
    try {
      if (!localStorage.getItem(key)) setShowEthosWelcome(true);
    } catch {
      // localStorage unavailable (private mode) — show it anyway, just won't persist
      setShowEthosWelcome(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, userEmail]);

  const dismissEthosWelcome = () => {
    try { localStorage.setItem(`no-ethos-seen:${userEmail || 'local'}`, '1'); } catch { /* ignore */ }
    setShowEthosWelcome(false);
  };

  const saveEmployeeProfile = async () => {
    // Mark done and close immediately — the popup must not re-appear even if the async save fails
    try { localStorage.setItem(`no-profile-done:${userEmail}`, '1'); } catch { /* ignore */ }
    setShowProfileDrawer(false);
    try {
      const list = await getAll('attendees');
      const existing = list.find(a => (a.email || '').toLowerCase() === userEmail.toLowerCase());
      const record = {
        ...(existing || { id: generateId(), name: userName, email: userEmail, rsvp: 'confirmed', role: 'Engineering' }),
        dietary: profileForm.dietary,
        bloodGroup: profileForm.bloodGroup,
        originCity: profileForm.originCity,
        phone: profileForm.phone,
        address: profileForm.address,
        birthday: profileForm.birthday,
        tshirtSize: profileForm.tshirtSize || 'M',
        roomNeeded: profileForm.roomNeeded || false,
        emergencyContactName: profileForm.emergencyContactName,
        emergencyContactPhone: profileForm.emergencyContactPhone,
        emergencyContactRelation: profileForm.emergencyContactRelation,
      };
      await put('attendees', record);
      window.dispatchEvent(new CustomEvent('planner:data-change'));
    } catch (e) {
      console.error('Profile save error', e);
    }
  };

  const handleSignOut = async () => {
    setUserMenuOpen(false);
    // Clear client-side Team Games state so the next sign-in starts fresh
    // (teams and scores reset — you have to reshuffle from scratch).
    resetTeamGamesState();
    if (usingFirebaseAuth && firebaseAuth && firebaseSignOut) await firebaseSignOut(firebaseAuth);
    else await signOutAuth();
  };

  if (isAuthMode && authLoading) {
    return (
      <div className="app-shell" style={{ alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
        <div style={{ width: 40, height: 40, border: '3px solid rgba(249,101,22,0.2)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <span style={{ color: 'var(--text2)', fontSize: 14 }}>Loading...</span>
      </div>
    );
  }

  if (isAuthMode && !session) {
    const onGoogleLogin = async () => {
      setAuthError('');
      try {
        if (usingFirebaseAuth) {
          if (!firebaseAuth || !firebaseSignInWithPopup || !firebaseGoogleProvider) {
            setAuthError('Authentication not ready. Please refresh and try again.');
            return;
          }
          // Update session immediately from the popup result. The
          // onAuthStateChanged listener can race on first load and miss this
          // sign-in, leaving the user stuck on the login screen until a manual
          // refresh — setting it here guarantees the app renders right away.
          const result = await firebaseSignInWithPopup(firebaseAuth, firebaseGoogleProvider);
          if (result?.user) {
            setSession({ user: result.user });
            setAuthLoading(false);
          }
        } else {
          const { error } = await signInWithGoogle();
          if (error) setAuthError(error.message || 'Unable to sign in. Please try again.');
        }
      } catch (err) {
        setAuthError(err.message || 'Unable to sign in. Please try again.');
      }
    };

    return (
      <div className="app-shell" style={{ alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div className="card" style={{ maxWidth: 460, width: '100%', textAlign: 'left', padding: 28 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
            <LogoFull height={38} />
            <div style={{ color: 'var(--text2)', fontSize: 13, marginTop: 2 }}>Corporate Meetup Operations · Planner</div>
          </div>
          <p style={{ color: 'var(--text2)', marginBottom: 16 }}>Sign in securely with Google SSO.</p>
          <button className="btn btn-secondary" style={{ width: '100%', justifyContent: 'center', fontWeight: 700 }} onClick={onGoogleLogin}>
            <span style={{ display: 'inline-flex', width: 20, height: 20 }}>
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.3-1.5 3.9-5.5 3.9-3.3 0-6-2.7-6-6s2.7-6 6-6c1.9 0 3.2.8 4 1.6l2.7-2.6C17 3.3 14.7 2.3 12 2.3 6.9 2.3 2.8 6.4 2.8 11.5S6.9 20.7 12 20.7c6.9 0 9.2-4.8 9.2-7.3 0-.5-.1-.9-.1-1.2H12z"/>
              </svg>
            </span>
            Continue with Google
          </button>
          {authError && (
            <div style={{ marginTop: 14, fontSize: 13, color: 'var(--danger)', background: 'rgba(194,65,12,0.08)', border: '1px solid rgba(194,65,12,0.2)', padding: '10px 12px', borderRadius: 8 }}>
              {authError}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (isAuthMode && userEmail && !canAccessApp(userEmail)) {
    return (
      <div className="app-shell" style={{ alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div className="card" style={{ maxWidth: 480, width: '100%' }}>
          <div className="card-title">Nativeorange access only</div>
          <p style={{ color: 'var(--text2)', marginBottom: 16 }}>Please sign in with your nativeorange.ai account to use the event planner.</p>
          <button className="btn btn-secondary" onClick={handleSignOut}>Sign Out</button>
        </div>
      </div>
    );
  }

  // ── Shared render helpers ─────────────────────────────────────────────────
  const renderUserDropdown = () => (
    <div className="topbar-user-dropdown">
      <div className="topbar-dropdown-header">
        <div className="topbar-avatar-circle large">{userInitials}</div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)', lineHeight: 1.2 }}>{userName}</div>
          {userEmail && <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userEmail}</div>}
          <span style={{ display: 'inline-block', marginTop: 5, background: 'rgba(249,101,22,0.1)', color: 'var(--accent)', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {userRole === 'admin' ? 'Admin' : 'Employee'}
          </span>
        </div>
      </div>
      <div className="topbar-dropdown-divider" />
      <button className="topbar-dropdown-item" onClick={() => { setActive('profile'); setUserMenuOpen(false); setSidebarOpen(false); if (appMode !== 'event-planner') setAppMode('event-planner'); }}>
        <UserCircle size={15} /> Profile Settings
      </button>
      {session && (
        <>
          <div className="topbar-dropdown-divider" />
          <button className="topbar-dropdown-item danger" onClick={handleSignOut}>
            <LogOut size={15} /> Sign Out
          </button>
        </>
      )}
    </div>
  );

  const renderProfileDrawer = () => (
    <>
      <div
        style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)', zIndex: 300 }}
        onClick={() => setShowProfileDrawer(false)}
      />
      <div className="profile-drawer">
        <div className="profile-drawer-header">
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--accent)', marginBottom: 3 }}>
              Welcome, {userName.split(' ')[0]}!
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text)' }}>Complete your profile</div>
            <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 4, lineHeight: 1.5 }}>
              Helps the team with logistics and ensures we have your emergency contact on file.
            </div>
          </div>
          <button className="btn btn-icon btn-secondary" onClick={() => setShowProfileDrawer(false)} style={{ flexShrink: 0 }}>
            <X size={18} />
          </button>
        </div>
        <div className="profile-drawer-body">
          <div>
            <div className="drawer-section-title">Personal Info</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group"><label>Phone Number</label><input value={profileForm.phone} onChange={e => setProfileForm(f => ({ ...f, phone: e.target.value }))} placeholder="+91 98xxx xxxxx" /></div>
                <div className="form-group"><label>Origin City / Hometown</label><input value={profileForm.originCity} onChange={e => setProfileForm(f => ({ ...f, originCity: e.target.value }))} placeholder="e.g. Bangalore" /></div>
              </div>
              <div className="form-group"><label>Current Address</label><input value={profileForm.address} onChange={e => setProfileForm(f => ({ ...f, address: e.target.value }))} placeholder="House/Flat, Street, City" /></div>
              <div className="form-group"><label>Birthday</label><input type="date" value={profileForm.birthday} onChange={e => setProfileForm(f => ({ ...f, birthday: e.target.value }))} /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label>Dietary Preference</label>
                  <select value={profileForm.dietary} onChange={e => setProfileForm(f => ({ ...f, dietary: e.target.value }))}>
                    {['Not specified','Vegetarian','Non-Vegetarian','Vegan','Jain'].map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Blood Group</label>
                  <select value={profileForm.bloodGroup} onChange={e => setProfileForm(f => ({ ...f, bloodGroup: e.target.value }))}>
                    {['Unknown','A+','A-','B+','B-','AB+','AB-','O+','O-'].map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label>T-Shirt Size</label>
                  <select value={profileForm.tshirtSize || 'M'} onChange={e => setProfileForm(f => ({ ...f, tshirtSize: e.target.value }))}>
                    {['XS','S','M','L','XL','XXL'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 0, paddingBottom: 8 }}>
                    <input type="checkbox" checked={!!profileForm.roomNeeded} onChange={e => setProfileForm(f => ({ ...f, roomNeeded: e.target.checked }))} style={{ width: 16, height: 16 }} />
                    <span>Need Stay Accommodation</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div>
            <div className="drawer-section-title">Emergency Contact</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group"><label>Contact Name</label><input value={profileForm.emergencyContactName} onChange={e => setProfileForm(f => ({ ...f, emergencyContactName: e.target.value }))} placeholder="Full name" /></div>
                <div className="form-group"><label>Contact Phone</label><input value={profileForm.emergencyContactPhone} onChange={e => setProfileForm(f => ({ ...f, emergencyContactPhone: e.target.value }))} placeholder="+91 98xxx xxxxx" /></div>
              </div>
              <div className="form-group">
                <label>Relationship</label>
                <select value={profileForm.emergencyContactRelation} onChange={e => setProfileForm(f => ({ ...f, emergencyContactRelation: e.target.value }))}>
                  {['Parent','Spouse','Sibling','Friend','Other'].map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>
        <div className="profile-drawer-footer">
          <button onClick={saveEmployeeProfile} className="btn btn-primary" style={{ flex: 1 }}>Save Profile</button>
          <button onClick={() => setShowProfileDrawer(false)} className="btn btn-secondary">Skip for now</button>
        </div>
      </div>
    </>
  );

  // ── Landing page (product hub) ─────────────────────────────────────────────
  if (appMode === null) {
    return (
      <>
        <div style={{ position: 'fixed', top: 20, right: 24, zIndex: 50 }}>
          <div className="topbar-user-wrap" ref={userMenuRef}>
            <button className="topbar-avatar-btn" onClick={() => setUserMenuOpen(p => !p)} aria-label="User menu">
              <div className="topbar-avatar-circle">{userInitials}</div>
              <div className="topbar-avatar-info">
                <span>{userName}</span>
                <small>{userRole === 'admin' ? 'Admin' : 'Employee'}</small>
              </div>
              <ChevronRight size={13} style={{ transform: userMenuOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s', color: 'var(--text3)', flexShrink: 0 }} />
            </button>
            {userMenuOpen && renderUserDropdown()}
          </div>
        </div>
        <Landing userName={userName} userRole={userRole} appAccess={appAccess} onSelect={setAppMode} />
        {showProfileDrawer && renderProfileDrawer()}
        {showEthosWelcome && !showProfileDrawer && (
          <EthosWelcome
            userName={userName}
            onClose={dismissEthosWelcome}
            onReadMore={() => { dismissEthosWelcome(); setAppMode('event-planner'); setActive('ethos'); }}
          />
        )}
        <Toaster />
        <ConfirmDialog />
      </>
    );
  }

  // ── Links Viewer (standalone, no event planner sidebar) ────────────────────
  if (appMode === 'links-viewer' && linksViewerAllowed) {
    return (
      <>
      <div className="app-shell">
        <main className="main-content" style={{ '--sidebar-w': '0px' }}>
          <header className="topbar">
            <button className="menu-btn" onClick={() => setSidebarOpen(true)}>
              <Menu size={22} />
            </button>
            <button
              onClick={() => setAppMode(null)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text2)', fontSize: 13, fontWeight: 600, padding: '6px 10px', borderRadius: 8, transition: 'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.color = 'var(--text)'; e.currentTarget.style.background = 'rgba(0,0,0,0.05)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text2)'; e.currentTarget.style.background = 'none'; }}
            >
              <Home size={15} /> Apps
            </button>
            <div className="topbar-title" style={{ marginLeft: 4 }}>Links Viewer</div>
            <TopbarCountdown />
            <div className="topbar-user-wrap" ref={userMenuRef}>
              <button className="topbar-avatar-btn" onClick={() => setUserMenuOpen(p => !p)} aria-label="User menu">
                <div className="topbar-avatar-circle">{userInitials}</div>
                <div className="topbar-avatar-info">
                  <span>{userName}</span>
                  <small>{userRole === 'admin' ? 'Admin' : 'Employee'}</small>
                </div>
                <ChevronRight size={13} style={{ transform: userMenuOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s', color: 'var(--text3)', flexShrink: 0 }} />
              </button>
              {userMenuOpen && renderUserDropdown()}
            </div>
          </header>
          <div className="page-wrap">
            <LinksViewer
              currentUser={{ name: userName, email: userEmail, role: userRole }}
              canManage={isAuthMode ? canManageLinks(userEmail) : true}
              canDelete={isAuthMode ? canDeleteLinks(userEmail) : true}
            />
          </div>
        </main>
      </div>
      {showProfileDrawer && renderProfileDrawer()}
      <Toaster />
      <ConfirmDialog />
      </>
    );
  }

  // ── Engineer Onboarding (standalone, no event planner sidebar) ─────────────
  if (appMode === 'onboarding-guide') {
    return (
      <>
      <div className="app-shell">
        <main className="main-content" style={{ '--sidebar-w': '0px' }}>
          <header className="topbar">
            <button
              onClick={() => setAppMode(null)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text2)', fontSize: 13, fontWeight: 600, padding: '6px 10px', borderRadius: 8, transition: 'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.color = 'var(--text)'; e.currentTarget.style.background = 'rgba(0,0,0,0.05)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text2)'; e.currentTarget.style.background = 'none'; }}
            >
              <Home size={15} /> Apps
            </button>
            <div className="topbar-title" style={{ marginLeft: 4 }}>Engineer Onboarding</div>
            <div className="topbar-user-wrap" ref={userMenuRef}>
              <button className="topbar-avatar-btn" onClick={() => setUserMenuOpen(p => !p)} aria-label="User menu">
                <div className="topbar-avatar-circle">{userInitials}</div>
                <div className="topbar-avatar-info">
                  <span>{userName}</span>
                  <small>{userRole === 'admin' ? 'Admin' : 'Employee'}</small>
                </div>
                <ChevronRight size={13} style={{ transform: userMenuOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s', color: 'var(--text3)', flexShrink: 0 }} />
              </button>
              {userMenuOpen && renderUserDropdown()}
            </div>
          </header>
          <div className="page-wrap">
            <OnboardingApp
              currentUser={{ name: userName, email: userEmail, role: userRole }}
              isAdmin={userRole === 'admin'}
            />
          </div>
        </main>
      </div>
      {showProfileDrawer && renderProfileDrawer()}
      <Toaster />
      <ConfirmDialog />
      </>
    );
  }

  return (
    <>
    <div className="app-shell">
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''} ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <div className="sidebar-header">
          {sidebarCollapsed
            ? <LogoMark size={30} />
            : (
              <div className="logo-svg-wrap">
                <LogoFull height={33} />
                <span className="logo-planner-badge">Planner</span>
              </div>
            )
          }
          <button className="close-btn" onClick={() => setSidebarOpen(false)}><X size={18} /></button>
          <button
            className="sidebar-collapse-btn"
            onClick={() => setSidebarCollapsed(p => !p)}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? <ChevronRight size={15} /> : <ChevronLeft size={15} />}
          </button>
        </div>
        <nav className="sidebar-nav">
          {/* Back to app hub — only shown when there's more than one app to switch to */}
          {hasAppHub && (
            <>
              <button
                className="nav-item"
                onClick={() => { setAppMode(null); setSidebarOpen(false); }}
                title={sidebarCollapsed ? 'All Apps' : undefined}
                style={{ color: 'var(--text2)', marginBottom: 4 }}
              >
                <Home size={18} />
                {!sidebarCollapsed && <span>All Apps</span>}
              </button>
              <div style={{ height: 1, background: 'var(--border)', margin: '4px 12px 8px' }} />
            </>
          )}
          {visibleNav.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              className={`nav-item ${active === id ? 'active' : ''}`}
              onClick={() => { setActive(id); setSidebarOpen(false); }}
              title={sidebarCollapsed ? label : undefined}
            >
              <Icon size={18} />
              {!sidebarCollapsed && <span>{label}</span>}
            </button>
          ))}
        </nav>
        {/* Profile link at bottom of sidebar */}
        <div className="sidebar-footer-nav">
          <button
            className={`nav-item ${active === 'profile' ? 'active' : ''}`}
            onClick={() => { setActive('profile'); setSidebarOpen(false); }}
            title={sidebarCollapsed ? 'Profile' : undefined}
          >
            <UserCircle size={18} />
            {!sidebarCollapsed && <span>Profile</span>}
          </button>
        </div>
      </aside>

      {sidebarOpen && <div className="overlay" onClick={() => setSidebarOpen(false)} />}

      <main className="main-content" data-sidebar-collapsed={sidebarCollapsed}>
        <header className="topbar">
          <button className="menu-btn" onClick={() => setSidebarOpen(true)}>
            <Menu size={22} />
          </button>
          <div className="topbar-title">
            {[...NAV, { id: 'profile', label: 'Profile' }].find(n => n.id === active)?.label}
          </div>

          <TopbarCountdown />

          {/* User avatar + dropdown */}
          <div className="topbar-user-wrap" ref={userMenuRef}>
            <button
              className="topbar-avatar-btn"
              onClick={() => setUserMenuOpen(p => !p)}
              aria-label="User menu"
            >
              <div className="topbar-avatar-circle">{userInitials}</div>
              <div className="topbar-avatar-info">
                <span>{userName}</span>
                <small>{userRole === 'admin' ? 'Admin' : 'Employee'}</small>
              </div>
              <ChevronRight size={13} style={{ transform: userMenuOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s', color: 'var(--text3)', flexShrink: 0 }} />
            </button>

            {userMenuOpen && renderUserDropdown()}
          </div>
        </header>

        <div className="page-wrap">
          <div className="page-anim" key={active}>
            <Page currentUser={{ name: userName, email: userEmail, role: userRole }} readOnly={userRole !== 'admin'} />
          </div>
        </div>
      </main>

      <button className="ask-planner-fab" onClick={() => setPlannerOpen(true)} title="Ask Planner">
        <Bot size={24} />
      </button>

      <AskPlanner open={plannerOpen} onClose={() => setPlannerOpen(false)} currentUser={{ name: userName, email: userEmail, role: userRole }} />

      {/* Ethos Welcome — first sign-in (waits until profile prompt is dismissed) */}
      {showEthosWelcome && !showProfileDrawer && (
        <EthosWelcome
          userName={userName}
          onClose={dismissEthosWelcome}
          onReadMore={() => { dismissEthosWelcome(); setActive('ethos'); setSidebarOpen(false); }}
        />
      )}

      {/* Profile Completion Drawer */}
      {showProfileDrawer && renderProfileDrawer()}
    </div>
    <Toaster />
    <ConfirmDialog />
    </>
  );
}
